/* ============================================================
 * frontend-v3/src/screens/compliance/Compliance.tsx
 *
 * T316 / V3I-FR-005 — Category A4 decomposition: Compliance check.
 *
 * Per PRD §7.1 A4 the surface binds the per-asset compliance read:
 *   useQuery(api.compliance.checkAsset, { assetId })
 *
 * The aggregate cross-asset roll-up has no public query family yet
 * (the prior `operations/_bundle.tsx:590` surface rendered an honest
 * "Próximamente — Cumplimiento agregado" empty state for exactly that
 * reason). A4's contract per the PRD is the per-asset read; the wired
 * surface accepts an `assetId` prop (route or parent-supplied) and
 * shows the real check result.
 *
 * Args contract (verified at convex/compliance.ts:111):
 *   assetId: Id<"assets">  (required)
 *   config?: <object>      (optional override; not used here)
 *
 * No mutation-policy entry (A4 is a query — the retry policy does not
 * apply to queries; Convex re-runs queries reactively).
 * ============================================================ */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Btn, EmptyState, PageHead, Panel, Pill, Skeleton,
} from '../../components/primitives';
import { navigate } from '../../lib/nav';
import { MockBoundary } from '../../components/Provenance';

interface ComplianceProps {
  assetId?: string;
}

type ComplianceRule = {
  id: string;
  name: string;
  description: string;
  status: 'pass' | 'fail' | 'warning';
  details?: string;
  autoFixable?: boolean;
  severity?: 'info' | 'warning' | 'error';
};

type ComplianceResult = {
  overallStatus?: 'pass' | 'fail' | 'warning';
  passedCount?: number;
  failedCount?: number;
  warningCount?: number;
  rules?: ComplianceRule[];
  checkedAt?: number;
  markerCount?: number;
};

export function Compliance({ assetId }: ComplianceProps) {
  const result = useQuery(
    api.compliance.checkAsset,
    assetId
      ? { assetId: assetId as unknown as Id<'assets'> }
      : 'skip',
  ) as ComplianceResult | undefined;

  // Without an assetId we render the honest aggregate-not-yet empty state
  // from the prior surface — the per-asset path is the operative contract
  // of A4; the aggregate is out of scope until the roll-up query lands.
  if (!assetId) {
    return (
      <div>
        <PageHead
          title="Cumplimiento"
          description="Asegúrate de que cada video cumpla tus reglas antes de salir al aire."
        />
        {/* No public aggregate compliance query exists yet — this whole
            workspace-wide view is a placeholder until the roll-up query
            family lands. The per-asset path (assetId present) is real. */}
        <MockBoundary detail="No existe aún una consulta agregada de cumplimiento en el backend; esta vista es un marcador de posición.">
        <Panel>
          <EmptyState
            icon="shield"
            title="Próximamente — Cumplimiento agregado"
            body="La vista agregada de cumplimiento llegará cuando se publiquen las consultas agregadas correspondientes. Mientras tanto, las reglas se verifican por video al subirlo y antes de cada publicación."
            action={
              <Btn
                variant="ghost"
                iconRight="arrowRight"
                onClick={() => navigate('/library')}
              >
                Ir a la biblioteca
              </Btn>
            }
          />
        </Panel>
        </MockBoundary>
      </div>
    );
  }

  if (result === undefined) {
    return <Skeleton h={420} />;
  }

  const overall = result.overallStatus ?? 'pass';
  const overallTone =
    overall === 'pass' ? 'good' : overall === 'warning' ? 'warn' : 'bad';

  return (
    <div>
      <PageHead
        title="Cumplimiento"
        description="Verificación por video — reglas evaluadas sobre los marcadores actuales."
      />
      <Panel
        title="Resumen"
        right={<Pill tone={overallTone} size="sm">{overall}</Pill>}
      >
        <div className="circa-grid-3" style={{ gap: 16 }}>
          <div>
            <div
              className="t-mono"
              style={{ fontSize: 11, color: 'var(--muted-slate)' }}
            >
              Aprobadas
            </div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>
              {result.passedCount ?? 0}
            </div>
          </div>
          <div>
            <div
              className="t-mono"
              style={{ fontSize: 11, color: 'var(--muted-slate)' }}
            >
              Advertencias
            </div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>
              {result.warningCount ?? 0}
            </div>
          </div>
          <div>
            <div
              className="t-mono"
              style={{ fontSize: 11, color: 'var(--muted-slate)' }}
            >
              Fallidas
            </div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>
              {result.failedCount ?? 0}
            </div>
          </div>
        </div>
      </Panel>
      <Panel title="Reglas" pad={0} style={{ marginTop: 16 }}>
        {(result.rules ?? []).length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState
              icon="shield"
              title="Sin reglas evaluadas"
              body="No hay reglas para mostrar en este video."
            />
          </div>
        ) : (
          (result.rules ?? []).map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                padding: '12px 20px',
                borderBottom: '1px solid var(--lightest-gray)',
              }}
            >
              <Pill
                tone={
                  r.status === 'pass'
                    ? 'good'
                    : r.status === 'warning'
                      ? 'warn'
                      : 'bad'
                }
                size="sm"
              >
                {r.status}
              </Pill>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--muted-slate)',
                    marginTop: 4,
                  }}
                >
                  {r.details ?? r.description}
                </div>
              </div>
            </div>
          ))
        )}
      </Panel>
    </div>
  );
}

export default Compliance;
